import * as Sentry from '@sentry/react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import Markdown from 'markdown-to-jsx';

import {
  SplashNotification,
  SplashNotificationSubmitActionType,
  useSplashNotifications,
} from '@queries/UserQueries';

import {
  SubmitSplashNotificationResponsePayload,
  useMarkSplashNotificationAsReadMutation,
  useSubmitSplashNotificationResponseMutation,
} from '@mutations/UserMutations';

import { useAnalytics } from '@hooks/useAnalytics';
import { usePWA } from '@hooks/usePWA';
import { useReferredBy } from '@hooks/useReferredBy';

import { useLocation, useNavigate, useParams } from '@router/index';

import { isValidObjectId } from '@utilities/isValidObjectId';
import { mapStringToParagraphs } from '@utilities/mapStringToParagraphs';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { MultipleChoiceList } from '@components/form/MultipleChoiceList';
import { Textarea } from '@components/form/Textarea';
import { ErrorBoundary } from '@components/layout/ErrorBoundary';
import { Popup, usePopupContext } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

const ACTION_CONTENT_COMPONENT_RECORD: Record<
  SplashNotificationSubmitActionType,
  FC<{
    splashNotification: SplashNotification;
    isSubmitting: boolean;
    onSubmit: (value: string[]) => Promise<void>;
  }>
> = {
  [SplashNotificationSubmitActionType.YesNo]: ({ splashNotification, isSubmitting, onSubmit }) => {
    const [yesOption, noOption] = splashNotification.submit?.options ?? [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ];

    return (
      <div className="d-grid gap-1">
        <div>
          <Button
            roundness="rounded"
            disabled={isSubmitting}
            onClick={() => onSubmit([yesOption.value])}
          >
            {yesOption.label}
          </Button>
        </div>
        <div>
          <Button
            roundness="rounded"
            variant="flat"
            color="neutral"
            disabled={isSubmitting}
            onClick={() => onSubmit([noOption.value])}
          >
            {noOption.label}
          </Button>
        </div>
      </div>
    );
  },
  [SplashNotificationSubmitActionType.TextInput]: ({
    splashNotification,
    isSubmitting,
    onSubmit,
  }) => {
    const [value, setValue] = useState('');

    return (
      <div className="d-grid gap-2">
        <Input
          name="splash-notification-text-input"
          placeholder={splashNotification.submit?.placeholder}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button
          className="mt-2"
          disabled={!value || isSubmitting}
          roundness="rounded"
          onClick={() => onSubmit([value])}
        >
          {splashNotification?.button?.text ?? 'Submit'}
        </Button>
      </div>
    );
  },
  [SplashNotificationSubmitActionType.TextArea]: ({
    splashNotification,
    isSubmitting,
    onSubmit,
  }) => {
    const [value, setValue] = useState('');

    return (
      <div className="d-grid gap-2">
        <Textarea
          name="splash-notification-textarea"
          placeholder={splashNotification.submit?.placeholder}
          onChange={setValue}
        />
        <Button
          className="mt-2"
          disabled={!value || isSubmitting}
          roundness="rounded"
          onClick={() => onSubmit([value])}
        >
          {splashNotification?.button?.text ?? 'Submit'}
        </Button>
      </div>
    );
  },
  [SplashNotificationSubmitActionType.MultipleChoice]: ({
    splashNotification,
    isSubmitting,
    onSubmit,
  }) => {
    const [selectedOption, setSelectedOption] = useState<string>();

    return (
      <div className="d-grid gap-2">
        <MultipleChoiceList
          options={splashNotification.submit?.options ?? []}
          onChange={([value]) => setSelectedOption(value)}
        />
        <Button
          className="mt-2"
          disabled={!selectedOption || isSubmitting}
          roundness="rounded"
          onClick={() => onSubmit([selectedOption])}
        >
          {splashNotification?.button?.text ?? 'Submit'}
        </Button>
      </div>
    );
  },
};

const SplashNotificationAction: FC<{
  splashNotification: SplashNotification;
}> = ({ splashNotification }) => {
  const { popupRef } = usePopupContext();
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { referredBy } = useReferredBy();

  const isSubmittingRef = useRef(false);
  const { isSubmittingSplashNotificationResponse, submitSplashNotificationResponseAsync } =
    useSubmitSplashNotificationResponseMutation();
  const handleSubmit = async (value: string[]) => {
    let shouldClose = false;

    try {
      if (isSubmittingRef.current || isSubmittingSplashNotificationResponse) {
        return;
      }

      track('Splash Notification Submit Action', { ...splashNotification, value });

      isSubmittingRef.current = true;

      if (referredBy) {
        if (splashNotification?.submit) {
          if (!splashNotification?.submit?.meta) {
            splashNotification.submit.meta = {};
          }

          splashNotification.submit.meta.referredBy = referredBy;
        }
      }

      const payload = {
        ...(splashNotification?.submit?.meta ?? {}),
        splashNotificationId: splashNotification._id,
        submit: splashNotification.submit,
        value,
      };

      const response = await submitSplashNotificationResponseAsync(payload);

      shouldClose =
        splashNotification._id && splashNotification._id !== response?.splashNotification?._id;
    } catch (error) {
      Sentry.captureException(error, { data: splashNotification });
      console.log(error);
    } finally {
      isSubmittingRef.current = false;

      if (shouldClose) {
        popupRef.current?.close();
      }
    }
  };

  if (splashNotification.submit) {
    const ActionContentComponent = ACTION_CONTENT_COMPONENT_RECORD[splashNotification.submit.type];

    return (
      <ActionContentComponent
        key={splashNotification.submit?.key}
        splashNotification={splashNotification}
        isSubmitting={isSubmittingSplashNotificationResponse}
        onSubmit={handleSubmit}
      />
    );
  }

  if (splashNotification.button) {
    return (
      <Button
        roundness="rounded"
        onClick={() => {
          track('Splash Notification Button Clicked', splashNotification);
          popupRef.current?.close();
          if (splashNotification.button?.to) {
            navigate(splashNotification.button.to);
          }
          splashNotification.button?.action?.();
        }}
      >
        {splashNotification.button.text}
      </Button>
    );
  }

  return <></>;
};

const SplashNotificationImage: FC<{ splashNotification: SplashNotification }> = ({
  splashNotification,
}) => {
  if (splashNotification?.fullSizeImageUrl) {
    return (
      <div className="pt-4">
        <img
          src={splashNotification.fullSizeImageUrl}
          style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }}
        />
      </div>
    );
  }

  if (splashNotification?.imageUrl) {
    return (
      <div className="pt-4">
        <img
          src={splashNotification.imageUrl}
          style={{ display: 'block', height: 120, margin: '0 auto' }}
        />
      </div>
    );
  }

  return <></>;
};

export const SplashNotificationPopup: FC<{ staticPath: string; dynamicPath: string }> = ({
  staticPath,
  dynamicPath,
}) => {
  const { splashNotifications = [] } = useSplashNotifications();
  const [readRecord, setReadRecord] = useState<Record<string, boolean>>({});
  const { track } = useAnalytics();

  const splashNotification = useMemo<SplashNotification | undefined>(() => {
    const currentSplashNotification = splashNotifications
      .filter((notification) => !readRecord[notification._id])
      .find(
        (notification) =>
          notification.displayPath === '*' ||
          notification.displayPath === staticPath ||
          notification.displayPath === dynamicPath,
      );

    return currentSplashNotification;
  }, [splashNotifications, readRecord, staticPath, dynamicPath]);

  const { markSplashNotificationAsReadAsync } = useMarkSplashNotificationAsReadMutation();
  useEffect(() => {
    if (isValidObjectId(splashNotification?._id)) {
      track('Splash Notification Displayed', splashNotification);
      markSplashNotificationAsReadAsync({ notificationId: splashNotification._id });
    }
  }, [splashNotification?._id]);

  const handleClose = () => {
    track('Splash Notification Close');
    if (splashNotification) {
      setReadRecord((prev) => ({ ...prev, [splashNotification._id]: true }));
    }
  };

  return (
    <Popup
      key={splashNotification?._id}
      isVisible={!!splashNotification?._id}
      onClose={handleClose}
      height={splashNotification?.height}
    >
      <Popup.Content>
        <ErrorBoundary
          onError={() => {
            console.log('onError', { splashNotification });
          }}
        >
          {splashNotification && (
            <>
              <SplashNotificationImage
                key={splashNotification?.fullSizeImageUrl ?? splashNotification?.imageUrl}
                splashNotification={splashNotification}
              />
              <TitleToolbar text={splashNotification.title} size="md" />
              {splashNotification.htmlContent ? (
                <Markdown options={{ forceWrapper: true, forceBlock: true }}>
                  {splashNotification.htmlContent}
                </Markdown>
              ) : (
                mapStringToParagraphs(splashNotification.content, { collapseTopMargin: true })
              )}
              <SplashNotificationAction splashNotification={splashNotification} />
            </>
          )}
        </ErrorBoundary>
      </Popup.Content>
    </Popup>
  );
};
